import React, { useMemo } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  ScrollView 
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { PaymentModal, PaymentModalContent } from '@/components/residents/payments';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RNButton } from '@/components';
import { useAppDispatch, useAppSelector } from '@/store';
import { 
  setPayingWithSourceId,
  setPayingForResidentId,
  setIsPaymentModalOpen,
} from '@/store/slices/residents/paymentsSlice';
import { useGetAutoPayStatusQuery } from '@/store/api';
import { formatCurrency } from '@/utils/common/formatting';
import { 
  getPaymentMethodLabel, 
  generatePaymentSummaryRows 
} from '@/utils/residents/paymentUtils';

type Params = {
  leaseId: number;
  residentId?: number;
  amount?: number;
  paymentMethod?: any;
  calculatedFees?: any;
  onBehalfOfName?: string;
};

export const TransactionReceivedScreen: React.FC = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  
  const { 
    leaseId, 
    residentId, 
    amount, 
    paymentMethod, 
    calculatedFees,
    onBehalfOfName 
  } = (route.params || {}) as Params;

  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const userType = useAppSelector((s) => (s as any).auth?.user?.type);

  // Determine if this is owner payment
  const isOwner = useMemo(() => {
    return !residentId || residentId === authUser?.id;
  }, [residentId, authUser]);

  // Payment summary rows
  const summaryRows = useMemo(() => {
    if (!calculatedFees) return [];
    
    const displayName = isOwner 
      ? `${authUser?.firstName || ''} ${authUser?.lastName || ''}`.trim()
      : onBehalfOfName || 'Resident';
    
    return generatePaymentSummaryRows(
      isOwner,
      displayName,
      paymentMethod,
      amount || 0,
      calculatedFees.convenienceFee || 0,
      calculatedFees.total || amount || 0
    );
  }, [isOwner, authUser, onBehalfOfName, paymentMethod, amount, calculatedFees]);

  // Check if autopay is already set up for this lease
  const { data: autoPayStatus, isLoading: isLoadingAutoPay } = useGetAutoPayStatusQuery();
  
  const hasAutoPaySetUp = autoPayStatus?.response?.length > 0 || false;

  const resetPaymentState = () => {
    dispatch(setPayingWithSourceId(0));
    dispatch(setPayingForResidentId(0));
    dispatch(setIsPaymentModalOpen(false));
  };

  const handleClose = () => {
    resetPaymentState();
    // Navigate back to payments screen
    navigation.replace('Payments');
  };

  const handleSetupAutoPay = () => {
    resetPaymentState();
    // Navigate to AutoPay wizard modal - matching web app flow
    navigation.replace('AutoPayWizard', { 
      leaseId, 
      residentId,
      fromTransactionReceived: true 
    });
  };

  const getConfirmationMessage = () => {
    if (userType === 'guest' && !isOwner) {
      const residentName = onBehalfOfName || 'the resident';
      return `Your transaction has been received. A confirmation has been sent to your email and to ${residentName}.`;
    }
    
    return `Your transaction has been received. A confirmation has been sent to ${authUser?.email || 'your email'}.`;
  };

  return (
    <PaymentModal title="Thank you!" onClose={handleClose} width={520}>
      <PaymentModalContent>
        <View style={styles.successHeader}>
          <SuccessIcon />
          <Text style={styles.title}>Thank you!</Text>
        </View>
        
        <Text style={styles.message}>
          {getConfirmationMessage()}
        </Text>
        
        {summaryRows.length > 0 && (
          <View style={styles.summarySection}>
            <Text style={styles.summaryTitle}>Payment Summary</Text>
            <View style={styles.summaryCard}>
              {summaryRows.map(([label, value], index) => (
                <View key={index} style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>{label}</Text>
                  <Text style={styles.summaryValue}>{value}</Text>
                </View>
              ))}
            </View>
          </View>
        )}
        
        <View style={styles.processingNote}>
          <InfoIcon />
          <Text style={styles.processingText}>
            Payments can take up to 5 business days to process completely. 
            Please be sure to keep sufficient funds in your account.
          </Text>
        </View>
        
        {!hasAutoPaySetUp && !isLoadingAutoPay && (
          <View style={styles.autoPaySection}>
            <Text style={styles.autoPayTitle}>
              Save time by setting up a recurring payment?
            </Text>
            <RNButton 
              title="Set up a recurring payment" 
              variant="primary" 
              onPress={handleSetupAutoPay}
              style={styles.autoPayButton}
            />
            <RNButton 
              title="No, thanks, I'm finished" 
              variant="link" 
              onPress={handleClose}
              style={styles.finishButton}
            />
          </View>
        )}
        
        {hasAutoPaySetUp && (
          <View style={styles.footer}>
            <RNButton 
              title="Close" 
              variant="primary" 
              onPress={handleClose}
              style={styles.closeButton}
            />
          </View>
        )}
      </PaymentModalContent>
    </PaymentModal>
  );
};

// Helper Icons
const SuccessIcon: React.FC = () => {
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).CHECK_CIRCLE;
  return Icon ? (
    <Icon width={64} height={64} color="#10B981" />
  ) : (
    <View style={styles.successIconFallback}>
      <Text style={styles.successIconText}>✓</Text>
    </View>
  );
};

const InfoIcon: React.FC = () => {
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).INFORMATION_CIRCLE;
  return Icon ? (
    <Icon width={20} height={20} color={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY} />
  ) : null;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  content: {
    flex: 1,
    padding: 24,
  },
  successHeader: {
    alignItems: 'center',
    marginBottom: 24,
  },
  successIconFallback: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#10B981',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  successIconText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: 'white',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginTop: 16,
    textAlign: 'center',
  },
  message: {
    fontSize: 16,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
  },
  summarySection: {
    marginBottom: 32,
  },
  summaryTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 16,
  },
  summaryCard: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 16,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  summaryLabel: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  processingNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 32,
  },
  processingText: {
    fontSize: 12,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    lineHeight: 18,
    marginLeft: 12,
    flex: 1,
  },
  autoPaySection: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 24,
    alignItems: 'center',
    marginBottom: 24,
  },
  autoPayTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    marginBottom: 20,
  },
  autoPayButton: {
    width: '100%',
    marginBottom: 12,
  },
  finishButton: {
    width: '100%',
  },
  footer: {
    alignItems: 'center',
    paddingTop: 24,
  },
  closeButton: {
    minWidth: 200,
  },
});

export default TransactionReceivedScreen;
